import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { apiFetch } from '@/lib/operator-api/base-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ViewKey = 'all' | 'needs_attention' | 'at_risk' | 'today' | 'resolved';

interface ConversationCustomer {
  id: string;
  name: string | null;
  phoneNumberE164: string;
}

interface ConversationBusiness {
  id: string;
  name: string;
}

interface Conversation {
  id: string;
  customerId: string;
  currentStep: string;
  lastMessageAt: string;
  messageCount: number;
  unreadCount: number;
  priorityScore: number;
  priorityTier: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  priorityState: string;
  recommendedAction: string;
  currentFlowStage: string;
  customer: ConversationCustomer;
  business: ConversationBusiness;
  createdAt: string;
  updatedAt: string;
}

interface ConversationStats {
  total: number;
  needs_attention: number;
  at_risk: number;
  today: number;
  resolved: number;
}

interface ConversationsApiResponse {
  success: boolean;
  conversations: Conversation[];
  nextCursor?: string;
  stats: ConversationStats;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VIEW_TABS: { key: ViewKey; label: string; statsKey: keyof ConversationStats }[] = [
  { key: 'all', label: 'All', statsKey: 'total' },
  { key: 'needs_attention', label: 'Needs Attention', statsKey: 'needs_attention' },
  { key: 'at_risk', label: 'At Risk', statsKey: 'at_risk' },
  { key: 'today', label: 'Today', statsKey: 'today' },
  { key: 'resolved', label: 'Resolved', statsKey: 'resolved' },
];

const PRIORITY_BADGE_VARIANT: Record<
  Conversation['priorityTier'],
  'destructive' | 'default' | 'secondary' | 'outline'
> = {
  CRITICAL: 'destructive',
  HIGH: 'default',
  MEDIUM: 'secondary',
  LOW: 'outline',
};

const EMPTY_STATS: ConversationStats = {
  total: 0,
  needs_attention: 0,
  at_risk: 0,
  today: 0,
  resolved: 0,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatFlowStage(stage: string): string {
  return stage.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function customerDisplayName(customer: ConversationCustomer): string {
  if (customer.name) return customer.name;
  return customer.phoneNumberE164;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mx-4 mt-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {message}
    </div>
  );
}

function EmptyState({ view }: { view: ViewKey }) {
  const labels: Record<ViewKey, string> = {
    all: 'No conversations yet',
    needs_attention: 'No conversations need attention',
    at_risk: 'No at-risk conversations',
    today: 'No conversations today',
    resolved: 'No resolved conversations',
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <div className="text-4xl mb-3">💬</div>
      <p className="text-sm">{labels[view]}</p>
    </div>
  );
}

function ViewTabs({
  activeView,
  stats,
  onViewChange,
}: {
  activeView: ViewKey;
  stats: ConversationStats;
  onViewChange: (view: ViewKey) => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto px-4 pt-4 pb-2">
      {VIEW_TABS.map(({ key, label, statsKey }) => {
        const count = stats[statsKey];
        const isActive = activeView === key;
        return (
          <Button
            key={key}
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewChange(key)}
            className="shrink-0"
          >
            {label}
            {count > 0 && (
              <span
                className={
                  isActive
                    ? 'ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1.5 text-xs'
                    : 'ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs'
                }
              >
                {count}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}

function ConversationCard({ conversation }: { conversation: Conversation }) {
  const navigate = useNavigate();
  const { customer, priorityTier, unreadCount, currentFlowStage, lastMessageAt, messageCount } =
    conversation;

  return (
    <Card
      className="transition-colors hover:bg-accent/50 cursor-pointer"
      onClick={() => navigate({ to: `/operator/conversations/${conversation.id}` })}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Left: customer info + flow stage */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">{customerDisplayName(customer)}</span>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="shrink-0 text-[10px] px-1.5 py-0">
                  {unreadCount}
                </Badge>
              )}
            </div>

            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="truncate">{formatFlowStage(currentFlowStage)}</span>
              <span aria-hidden>&#183;</span>
              <span className="shrink-0">{messageCount} msgs</span>
            </div>
          </div>

          {/* Right: priority + time */}
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge variant={PRIORITY_BADGE_VARIANT[priorityTier]} className="text-[10px]">
              {priorityTier}
            </Badge>
            <span className="text-[11px] text-muted-foreground">
              {formatRelativeTime(lastMessageAt)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface SmartInboxProps {
  businessId: string;
}

export function SmartInbox({ businessId }: SmartInboxProps) {
  const [view, setView] = useState<ViewKey>('all');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [stats, setStats] = useState<ConversationStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        businessId,
        view,
        limit: '30',
      });
      const response = await apiFetch(`/api/operator/conversations?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to load conversations (${response.status})`);
      }

      const data: ConversationsApiResponse = await response.json();

      if (!data.success) {
        throw new Error('Failed to load conversations');
      }

      setConversations(data.conversations);
      setStats(data.stats ?? EMPTY_STATS);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, view]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  return (
    <div className="flex flex-col h-full">
      <ViewTabs activeView={view} stats={stats} onViewChange={setView} />

      {error && <ErrorBanner message={error} />}

      <div className="flex-1 max-h-[calc(100vh-300px)] overflow-y-auto px-4 pb-4 pt-2 space-y-2">
        {loading && <LoadingSpinner />}

        {!loading && !error && conversations.length === 0 && <EmptyState view={view} />}

        {!loading &&
          conversations.map((conversation) => (
            <ConversationCard key={conversation.id} conversation={conversation} />
          ))}
      </div>
    </div>
  );
}
